import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DebounceInput } from 'react-debounce-input';
import { useSearchbyemil } from '../../hooks/searchbyemail';
import { useAddCM } from '../../hooks/useaddCandidateManager';
import { useGetOrganization } from '../../hooks/useGetOrganization';
import { BlockUI } from '../../components/Loader/BlockUI';
import useRemoveOrgManager from '../../hooks/useRemoveOrgManager';
import { useTranslation } from 'react-i18next';
import { useAddPCM } from '../../hooks/useaddPrimaryCandidateManager';

export interface OrgManager {
  Id: string;
  ContactId: string;
  ManagerName: string;
  IsPrimary: boolean;
}

export default function AddCandidateManager() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [q, setQ] = useState<string>('');
  const [selectedorgManagers, setSelectedorgManagers] = useState<any[]>([]);
  const { t } = useTranslation();

  const addCM = useAddCM();
  const addPCM = useAddPCM();
  const searchMutation = useSearchbyemil();
  const {
    data: organizationData,
    isLoading: organizationLoading,
    refetch: refetchOrganization,
  } = useGetOrganization(organizationId);

  const removeCM = useRemoveOrgManager();
  const [addErrorMessage, setAddErrorMessage] = useState("");

  const data = searchMutation.data?.data?.Data || [];

  useEffect(() => {
    if (q) {
      searchMutation.mutate({
        Query: q,
        PageNumber: 1,
        PageSize: 5,
      });
    }
  }, [q]);

  useEffect(() => {
    if (organizationData?.Data?.OrgManagers?.length > 0) {
      const initialManagers = organizationData.Data.OrgManagers.map((manager: OrgManager) => ({
        id: manager.Id,
        contactId: manager.ContactId,
        name: manager.ManagerName,
        primary: manager.IsPrimary,
      }));
      setSelectedorgManagers(initialManagers);
    }
  }, [organizationData]);

  if (organizationLoading) {
    // return <BlockUI title="" blocking={true} />;
  }

  return (
    <div id="main-content">
      <div className="cco_content_area cco_content_area_0">
        <article className="react-container">
          <div className="cco_section cco_section_0">
            <div className="cco_form_row">
              <div className="cco_form_col">
                <h2 className="cco_form_heading">
                  <strong>{t("MyCCOPro.OrganizationDetails.AddManagers.title")}</strong>
                </h2>
                <div className="cco_form_wrap">
                  <div className="cco_form_row">
                    {/* Search Area */}
                    <div className="cco_form_col cco_form_col_1">
                      <div className="cco_form_label">{t("MyCCOPro.OrganizationDetails.AddManagers.searchLabel")}</div>
                      <span className="text-muted small-text text-primary" style={{ fontSize: '13px' }}>
                        &nbsp; &nbsp; {t("MyCCOPro.OrganizationDetails.AddManagers.searchHint")}
                      </span>

                      <div className="cco_form_card">
                        <div className="cco_form_card_header">
                          <DebounceInput
                            type="text"
                            minLength={2}
                            debounceTimeout={300}
                            placeholder={t("MyCCOPro.OrganizationDetails.AddManagers.searchPlaceholder")}
                            className="cco_form_field"
                            onChange={(event) => {
                              setQ(event.target.value);
                              setAddErrorMessage("");
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#c67b05')}
                            onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                            style={{
                              padding: '10px 15px',
                              borderRadius: '25px',
                              border: '1px solid #ccc',
                              width: '100%',
                              outline: 'none',
                              fontSize: '16px',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                              transition: 'border-color 0.3s ease-in-out',
                            }}
                          />
                        </div>

                        <div className="cco_form_card_body">
                          {q.trim() !== '' && (
                            <>
                              {searchMutation.isLoading ? (
                                <p>{t("MyCCOPro.OrganizationDetails.AddManagers.searching")}</p>
                              ) : data.length > 0 ? (
                                data.map((orgManager: any) => (
                                  <div
                                    key={orgManager.ContactId}
                                    className="cco_form_fieldset cco_form_fieldset_0"
                                    style={{ marginBottom: '15px' }}
                                  >
                                    <div className="cco_form_fieldset_item cco_form_fieldset_item_large">
                                      {orgManager.FirstName} {orgManager.LastName}
                                    </div>

                                    {!selectedorgManagers.some((e) => e.id === orgManager.ContactId) && (
                                      <div className="cco_form_fieldset_item">
                                        <button
                                          type="button"
                                          className="cco_form_btn cco_form_btn_0"
                                          style={{ backgroundColor: '#c67b05', color: 'white' }}
                                          onClick={async () => {
                                            try {
                                              await addCM.mutateAsync({
                                                organizationId: organizationId || '',
                                                contactId: orgManager.ContactId,
                                                isPrimary: false,
                                              });

                                              // Refetch org managers
                                              await refetchOrganization();
                                            } catch (err: any) {
                                              const apiMessage = err.message || 'Unexpected error occurred.';
                                              const localizedMessageMap: Record<string, string> = {
                                                "Maximum organization manager positions reached.": t("organization.errors.maxOrgManager"),
                                                "Unexpected error occurred.": t("MyCCOPro.errors.unexpected"),
                                              };
                                              const localizedMessage = localizedMessageMap[apiMessage] || apiMessage;
                                              setAddErrorMessage(localizedMessage);
                                            }
                                          }}
                                        >
                                          {t("MyCCOPro.OrganizationDetails.AddManagers.select")}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))
                              ) : null}

                              {addErrorMessage && (
                                <p style={{ color: 'red', marginTop: '10px' }}>{addErrorMessage}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Selected Managers */}
                    <div className="cco_form_col cco_form_col_2">
                      <div className="cco_form_label">{t("MyCCOPro.OrganizationDetails.AddManagers.selectedLabel")}</div>
                      <div className="cco_form_card">
                        <div className="cco_form_card_body">
                          {selectedorgManagers.length > 0 ? (
                            selectedorgManagers.map((orgManager) => (
                              <div key={orgManager.id} className="cco_form_fieldset cco_form_fieldset_0">
                                <div className="cco_form_fieldset_item cco_form_fieldset_item_large">
                                  {orgManager.name}
                                </div>
                                <div className="cco_form_fieldset_item">
                                  <div className="cco_form_fieldset cco_form_fieldset_1">
                                    {orgManager.primary ? (
                                      <div className="cco_form_fieldset_item">
                                        <div className="cco_form_blurb cco_form_blurb_0">
                                          {t("MyCCOPro.OrganizationDetails.AddManagers.primary")}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div
                                          className="cco_form_fieldset_item"
                                          style={{ marginBottom: '20px', marginTop: '20px' }}
                                        >
                                          <button
                                            type="button"
                                            className="cco_form_btn cco_form_btn_0"
                                            style={{ backgroundColor: '#c67b05', color: 'white' }}
                                            onClick={async () => {
                                              try {
                                                await addPCM.mutateAsync({
                                                  organizationId: organizationId || '',
                                                  contactId: orgManager.contactId,
                                                });

                                                // Update all managers: only this one is primary
                                                setSelectedorgManagers((prev) =>
                                                  prev.map((mgr) => ({
                                                    ...mgr,
                                                    primary: mgr.contactId === orgManager.contactId,
                                                  }))
                                                );

                                                // Optionally refetch org again if needed
                                                await refetchOrganization();
                                              } catch (error: any) {
                                                alert(error.message || 'Failed to set primary. Please try again.');
                                              }
                                            }}
                                          >
                                            {t("MyCCOPro.OrganizationDetails.AddManagers.setPrimary")}
                                          </button>
                                        </div>

                                        {/* Remove button */}
                                        <div className="cco_form_fieldset_item" style={{ marginBottom: '20px', marginTop: '20px' }}>
                                          <button
                                            type="button"
                                            className="cco_form_btn cco_form_btn_1"
                                            style={{ paddingLeft: '10px' }}
                                            onClick={async () => {
                                              try {
                                                await removeCM.removeOrgManager(organizationId || '', orgManager.contactId);
                                                setSelectedorgManagers((prev) =>
                                                  prev.filter((_orgManager) => _orgManager.contactId !== orgManager.contactId)
                                                );
                                                await refetchOrganization();
                                              } catch (error) {
                                                alert("Failed to remove manager. Please try again.");
                                              }
                                            }}
                                          />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : null}
                          {addPCM.isError && (
                            <p style={{ color: 'red' }}>{(addPCM.error as Error).message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '30px' }}>
                    <div className="cco_btn cco_btn_2">
                      <a href={`/OrganizationInformation/${organizationId}`}>
                        {t("MyCCOPro.OrganizationDetails.AddManagers.back")}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
